import { friendlyLifecycleError } from '@/routes/Agency/Lifecycle/shared/labels';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface Paged<T> {
  data: T[];
  pagination: Pagination;
}

export type LifecycleMode = 'off' | 'shadow' | 'live';
export type LifecycleChannel = 'sms' | 'push' | 'email';
export type CopyLocale = 'en' | 'es';
export type CallTaskStatus = 'open' | 'completed' | 'skipped';
export type CallTaskOutcome =
  | 'completed_on_call'
  | 'will_finish'
  | 'not_interested'
  | 'no_answer'
  | 'booked'
  | 'document_issue';

// Of the active workers in the pilot group: how many each channel can reach
// (graphy JourneyStatsService#channel_reach). Optional: stats cached before
// the field existed come back without it for up to 5 minutes.
export interface ChannelReach {
  total: number;
  push_enabled: number;
  sms_opted_in: number;
  email_deliverable: number;
}

export interface LifecycleGlobal {
  journey_key: '_global';
  updated_at: string | null;
  // Fingerprint of the editable values; sent back on save so graphy answers
  // 409 when someone else saved first (bug hunt 19 BH-1908).
  version?: string;
  mode: LifecycleMode | null;
  cohort_start_on: string | null;
  cohort_zip_prefixes: string | null;
  recipient_allowlist: number[];
  allowlist_enforced: boolean;
  monthly_sms_budget_cents: number | null;
  sms_segment_cost_cents: number | null;
  tax_guide_url: string | null;
  last_sweep_at: string | null;
  last_dispatch_at: string | null;
  kill_switch_on: boolean;
  awaiting_review_count: number;
  channel_reach?: ChannelReach | null;
  spend_month_to_date_cents: number;
  stats_computed_at: string;
  heartbeat_stale: boolean;
}

export interface JourneyStep {
  key: string;
  kind: 'message' | 'call_task' | 'goal_message';
  label: string;
  offset_hours: number;
  day: number;
  channel: LifecycleChannel | 'ops';
  push_only: boolean;
  // The step's live English text, first variant (SMS, else push body, else
  // email subject), with %{placeholders}. Null for a call task.
  preview?: string | null;
}

export interface JourneyDefinition {
  group: 'profile' | 'compliance' | 'activation' | 'retention';
  enters: string;
  exits: string;
  skilled_only: boolean;
  steps: JourneyStep[];
}

export interface JourneyStatsWindow {
  enrolled: number;
  active: number;
  holdout: number;
  exited_goal: number;
  exited_window: number;
  exited_superseded: number;
  exited_suppressed: number;
  messages_sent: Record<string, number>;
  delivered: number;
  clicked: number;
  failed: number;
  skipped: Record<string, number>;
  sms_stop_count: number;
  sms_stop_rate: number;
  // The SMS sent in the window: the STOP rate's base. Absent from stats
  // cached before graphy sent it.
  sms_stop_base?: number;
  sms_segments: number;
  sms_cost_cents: number;
  cost_per_goal_cents: number | null;
  conversion_journey: number | null;
  conversion_holdout: number | null;
  p_value: number | null;
  // Journeys entered in the window and closed, per arm: the people the
  // conversion rates and p_value are computed over.
  closed_journey?: number;
  closed_holdout?: number;
  closed_journey_goals?: number;
}

export interface LifecycleJourney {
  journey_key: string;
  updated_at: string | null;
  version?: string;
  name: string;
  enabled: boolean;
  sms_enabled: boolean;
  sms_paused_at: string | null;
  sms_pause_reason: string | null;
  holdout_percent: number;
  high_demand_threshold: number | null;
  copy_pending: boolean;
  window_days: number;
  definition: JourneyDefinition;
  stats: { '7d': JourneyStatsWindow; '30d': JourneyStatsWindow };
}

export interface TransactionalMessage {
  step_key: string;
  // J6 is the J6.A shift alert texted when push is off (#47).
  journey_key: 'J1' | 'J6' | 'J8' | 'TX';
  label: string;
  trigger: string;
  channels: LifecycleChannel[];
  /** False for legal text the copy editor does not list (tx_sms_opt_in). */
  editable?: boolean;
}

export interface JourneysPayload {
  global: LifecycleGlobal;
  journeys: LifecycleJourney[];
  transactional: TransactionalMessage[];
}

export interface JourneyChanges {
  enabled?: boolean;
  sms_enabled?: boolean;
  holdout_percent?: number;
  high_demand_threshold?: number | string | null;
  clear_sms_pause?: boolean;
  mode?: LifecycleMode;
  cohort_start_on?: string | null;
  cohort_zip_prefixes?: string | null;
  recipient_allowlist?: number[];
  monthly_sms_budget_cents?: number | string | null;
  sms_segment_cost_cents?: number | string | null;
  tax_guide_url?: string | null;
  version?: string;
}

export interface JourneySettingsResponse {
  journey_key: string;
  updated_at: string | null;
  name: string;
  enabled: boolean;
  sms_enabled: boolean;
  sms_paused_at: string | null;
  sms_pause_reason: string | null;
  holdout_percent: number;
  high_demand_threshold: number | null;
  copy_pending: boolean;
  window_days: number;
  version?: string;
}

export interface GlobalSettingsResponse {
  journey_key: '_global';
  updated_at: string | null;
  mode: LifecycleMode | null;
  cohort_start_on: string | null;
  cohort_zip_prefixes: string | null;
  recipient_allowlist: number[];
  allowlist_enforced: boolean;
  monthly_sms_budget_cents: number | null;
  sms_segment_cost_cents: number | null;
  tax_guide_url: string | null;
  last_sweep_at: string | null;
  last_dispatch_at: string | null;
  version?: string;
}

export type JourneySettings = JourneySettingsResponse | GlobalSettingsResponse;

export interface CallTaskWorker {
  id: number;
  name: string;
  /** graphy sends user&.phone_number: a worker can have no phone. */
  phone_number: string | null;
  zip: string;
  city: string | null;
  open_shifts_nearby: number | null;
}

export interface CallTask {
  id: number;
  journey_key: string;
  journey_name: string | null;
  step_key: string;
  step_label: string;
  status: CallTaskStatus;
  due_at: string;
  outcome: CallTaskOutcome | null;
  preferred_roles: string | null;
  preferred_days: string | null;
  notes: string | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  completed_at: string | null;
  completed_by_user_id: number | null;
  completed_by_name: string | null;
  closed_by_system: boolean;
  do_not_call_reason: string | null;
  updated_at: string;
  // graphy's edit stamp; sent back on PATCH so a stale save gets a 409.
  version?: string | null;
  worker: CallTaskWorker;
}

export interface CallTaskDetail extends CallTask {
  script: string;
}

export interface CallTaskChanges {
  status?: CallTaskStatus;
  outcome?: CallTaskOutcome;
  preferred_roles?: string | null;
  preferred_days?: string | null;
  notes?: string | null;
  assigned_to_user_id?: number | null;
  version?: string;
}

export interface CallTaskFilters {
  status?: CallTaskStatus | '';
  journey_key?: string;
  assigned_to?: number | string;
  page?: number;
  per_page?: number;
}

export interface Assignee {
  user_id: number;
  name: string;
  role: 'tenant_admin' | 'helpdesk_admin';
}

export interface LifecycleEvent {
  kind: 'event';
  id: number;
  event_key: string;
  occurred_at: string;
  source: string;
  actor_user_id: number | null;
  actor_name: string | null;
  payload: Record<string, unknown>;
  at: string;
}

export interface EventFilters {
  event_key?: string;
  page?: number;
  per_page?: number;
}

export interface CopyListItem {
  step_key: string;
  label: string;
  journey_key: string;
  journey_name: string;
  transactional: boolean;
  channels: LifecycleChannel[];
  version: number;
  edited: boolean;
  has_draft: boolean;
  published_at: string | null;
  spanish_status: 'none' | 'draft' | 'published';
}

export type CopyContent = Record<string, Record<string, string>>;

export interface CopyDraft {
  content: CopyContent;
  base_version: number | null;
  updated_at: string;
  // Names the draft as this admin saw it: graphy refuses a save, publish or
  // discard sent with an older one (someone else changed the draft).
  token?: string;
  actor_name: string | null;
  structure_errors: string[];
  lint_errors: Record<string, string[]>;
}

export interface CopyHistoryItem {
  version: number;
  actor_name: string | null;
  published_at: string;
  reverted_from_version: number | null;
  reset: boolean;
  content: CopyContent | null;
}

export interface CopyDetail {
  step_key: string;
  label: string;
  journey_key: string;
  journey_name: string;
  transactional: boolean;
  channels: LifecycleChannel[];
  allowed_fields: Record<string, Record<string, string[]>>;
  editable_fields: Record<string, string[]>;
  locked: { sms_stop_line: string; email_footer: string };
  // remaining_steps_list is a list (CopyLint::REFERENCE).
  sample_values: Record<string, string | string[]>;
  locale: CopyLocale;
  version: number;
  default: Record<string, Record<string, unknown>>;
  override: CopyContent | null;
  current: Record<string, Record<string, unknown>>;
  english: Record<string, Record<string, unknown>> | null;
  draft: CopyDraft | null;
  history: CopyHistoryItem[];
}

export interface WorkerStage {
  key: string;
  step_id: string | null;
  step_name: string | null;
  since: string | null;
  days_in_stage: number | null;
  onboarding_pct: number;
  actionable: boolean;
  worked_shifts: number;
  upcoming_shifts: number;
}

export interface WorkerPreferences {
  sms_consent_at: string | null;
  sms_consent_source: string | null;
  sms_opted_out_at: string | null;
  sms_opt_out_source: string | null;
  email_unsubscribed_at: string | null;
  do_not_contact_at: string | null;
  do_not_contact_reason: string | null;
  journeys_paused_at: string | null;
  journeys_paused_by: string | null;
  journeys_pause_reason: string | null;
  /** A STOP the phone number sent (Shift Blast, carrier, before this engine). */
  sms_phone_opt_out?: {
    source: string | null;
    opted_out_at: string | null;
  } | null;
  push_reachable: boolean;
  /**
   * False when sends would skip the email: blank, or the funnel placeholder
   * @gravywork.temp. Optional because older graphy payloads lack it.
   */
  email_deliverable?: boolean;
}

export interface WorkerJourney {
  id: number;
  journey_key: string;
  journey_name: string;
  entry_number: number;
  status: string;
  holdout: boolean;
  stage_started_at: string | null;
  entered_at: string;
  window_ends_at: string | null;
  ended_at: string | null;
  exit_reason: string | null;
  day: number | null;
  window_days: number;
  next_message: {
    id: number;
    step_key: string;
    channel_planned: LifecycleChannel;
    scheduled_for: string;
  } | null;
}

export interface WorkerLifecycleOverview {
  stage: WorkerStage;
  suppressed_reason: string | null;
  preferences: WorkerPreferences;
  journeys: WorkerJourney[];
  open_call_tasks: number;
}

export interface MessageTimelineItem {
  kind: 'message';
  id: number;
  journey_key: string;
  step_key: string;
  category: 'journey' | 'transactional';
  channel_planned: LifecycleChannel;
  channel_used: LifecycleChannel | null;
  status: string;
  scheduled_for: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  clicked_at: string | null;
  skip_reason: string | null;
  fallback_reason: string | null;
  locale: CopyLocale;
  locale_fallback: boolean;
  sms_segments: number | null;
  cost_cents: number;
  variant: string | null;
  body_preview: string | null;
  at: string;
}

export interface EventTimelineItem {
  kind: 'event';
  id: number;
  event_key: string;
  occurred_at: string;
  source: string;
  actor_user_id: number | null;
  actor_name: string | null;
  payload: Record<string, unknown>;
  at: string;
}

export interface LegacyTimelineItem {
  kind: 'legacy';
  id: number;
  source: 'sms_logs' | 'firebase_worker_logs';
  status: string;
  sent_at: string | null;
  body_preview: string | null;
  at: string;
  sms_type?: string | null;
  key?: string | null;
  redirect?: string | null;
}

export type TimelineItem =
  | MessageTimelineItem
  | EventTimelineItem
  | LegacyTimelineItem;

export interface WorkerMessageFilters {
  kind?: 'message' | 'event' | 'legacy' | '';
  journey_key?: string;
  // One status or several (sent as status[]=…).
  status?: string | string[];
  // Messages that went out (have a sent_at), whatever their status became.
  sent?: boolean;
  channel?: LifecycleChannel | '';
  page?: number;
  per_page?: number;
}

type Params = Record<string, unknown>;

const compact = (params: Params = {}): Params =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

// null means "I saw no draft"; undefined sends nothing (no check).
const draftTokenParam = (token?: string | null): Params =>
  token === undefined ? {} : { draft_token: token };

const localeParam = (locale?: CopyLocale): Params =>
  locale === 'es' ? { locale } : {};

const COPY = '/api/admin/lifecycle/copy';
const workerPath = (workerId: number | string) =>
  `/api/admin/workers/${workerId}`;

export const lifecycleAPI = {
  getJourneys: async ({
    refresh,
  }: { refresh?: boolean } = {}): Promise<JourneysPayload> => {
    const response = await api.get('/api/admin/lifecycle/journeys', {
      params: refresh ? { refresh: 1 } : {},
    });
    return response.data.data;
  },

  updateJourney: async (
    journeyKey: string,
    changes: JourneyChanges,
  ): Promise<JourneySettings> => {
    const response = await api.patch(
      `/api/admin/lifecycle/journeys/${journeyKey}`,
      changes,
    );
    return response.data.data;
  },

  listCallTasks: async (
    filters: CallTaskFilters = {},
  ): Promise<Paged<CallTask>> => {
    const response = await api.get('/api/admin/lifecycle/call_tasks', {
      params: compact({ ...filters }),
    });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getCallTask: async (id: number): Promise<CallTaskDetail> => {
    const response = await api.get(`/api/admin/lifecycle/call_tasks/${id}`);
    return response.data.data;
  },

  updateCallTask: async (
    id: number,
    changes: CallTaskChanges,
  ): Promise<CallTask> => {
    const response = await api.patch(
      `/api/admin/lifecycle/call_tasks/${id}`,
      changes,
    );
    return response.data.data;
  },

  listAssignees: async (): Promise<Assignee[]> => {
    const response = await api.get('/api/admin/lifecycle/assignees');
    return response.data.data;
  },

  listEvents: async (
    filters: EventFilters = {},
  ): Promise<Paged<LifecycleEvent>> => {
    const response = await api.get('/api/admin/lifecycle/events', {
      params: compact({ ...filters }),
    });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  listCopy: async ({ locale }: { locale?: CopyLocale } = {}): Promise<
    CopyListItem[]
  > => {
    const response = await api.get(COPY, { params: localeParam(locale) });
    return response.data.data;
  },

  getCopy: async (
    stepKey: string,
    { locale }: { locale?: CopyLocale } = {},
  ): Promise<CopyDetail> => {
    const response = await api.get(`${COPY}/${stepKey}`, {
      params: localeParam(locale),
    });
    return response.data.data;
  },

  saveCopyDraft: async (
    stepKey: string,
    {
      content,
      version,
      locale,
      draftToken,
    }: {
      content: CopyContent;
      version: number;
      locale?: CopyLocale;
      draftToken?: string | null;
    },
  ): Promise<CopyDetail> => {
    const response = await api.put(`${COPY}/${stepKey}`, {
      content,
      version,
      ...localeParam(locale),
      ...draftTokenParam(draftToken),
    });
    return response.data.data;
  },

  discardCopyDraft: async (
    stepKey: string,
    {
      locale,
      draftToken,
    }: { locale?: CopyLocale; draftToken?: string | null } = {},
  ): Promise<CopyDetail> => {
    const response = await api.delete(`${COPY}/${stepKey}/draft`, {
      data: { ...localeParam(locale), ...draftTokenParam(draftToken) },
    });
    return response.data.data;
  },

  publishCopy: async (
    stepKey: string,
    {
      version,
      locale,
      draftToken,
    }: { version: number; locale?: CopyLocale; draftToken?: string | null },
  ): Promise<CopyDetail> => {
    const response = await api.post(`${COPY}/${stepKey}/publish`, {
      version,
      ...localeParam(locale),
      ...draftTokenParam(draftToken),
    });
    return response.data.data;
  },

  revertCopy: async (
    stepKey: string,
    {
      to_version,
      version,
      locale,
    }: { to_version: number; version: number; locale?: CopyLocale },
  ): Promise<CopyDetail> => {
    const response = await api.post(`${COPY}/${stepKey}/revert`, {
      to_version,
      version,
      ...localeParam(locale),
    });
    return response.data.data;
  },

  resetCopy: async (
    stepKey: string,
    { version, locale }: { version: number; locale?: CopyLocale },
  ): Promise<CopyDetail> => {
    const response = await api.delete(`${COPY}/${stepKey}`, {
      data: { version, ...localeParam(locale) },
    });
    return response.data.data;
  },

  getWorkerOverview: async (
    workerId: number | string,
  ): Promise<WorkerLifecycleOverview> => {
    const response = await api.get(`${workerPath(workerId)}/journeys`);
    return response.data.data;
  },

  pauseJourneys: async (
    workerId: number | string,
    reason: string,
  ): Promise<WorkerLifecycleOverview> => {
    const response = await api.post(`${workerPath(workerId)}/journeys/pause`, {
      reason,
    });
    return response.data.data;
  },

  resumeJourneys: async (
    workerId: number | string,
  ): Promise<WorkerLifecycleOverview> => {
    const response = await api.post(`${workerPath(workerId)}/journeys/resume`);
    return response.data.data;
  },

  enrolJourney: async (
    workerId: number | string,
    journeyKey: string,
  ): Promise<WorkerLifecycleOverview> => {
    const response = await api.post(`${workerPath(workerId)}/journeys/enrol`, {
      journey_key: journeyKey,
    });
    return response.data.data;
  },

  setDoNotContact: async (
    workerId: number | string,
    body: { do_not_contact: boolean; reason?: string },
  ): Promise<WorkerLifecycleOverview> => {
    const response = await api.patch(
      `${workerPath(workerId)}/messaging_preferences`,
      body,
    );
    return response.data.data;
  },

  listWorkerMessages: async (
    workerId: number | string,
    filters: WorkerMessageFilters = {},
  ): Promise<Paged<TimelineItem>> => {
    const response = await api.get(`${workerPath(workerId)}/messages`, {
      params: compact({ ...filters }),
    });
    return { data: response.data.data, pagination: response.data.pagination };
  },
};

interface ErrorLike {
  message?: string;
  response?: { status?: number; data?: { errors?: string[]; error?: string } };
}

export const lifecycleErrorMessage = (
  err: unknown,
  fallback: string,
): string => {
  const error = (err ?? {}) as ErrorLike;
  const data = error.response?.data;
  const errors = data?.errors ?? [];
  const message = (errors.length > 0 ? errors.join('; ') : '') || data?.error;
  return message ? friendlyLifecycleError(message) : fallback;
};

export const isConflict = (err: unknown): boolean =>
  (err as ErrorLike | null | undefined)?.response?.status === 409;

export const isNotFound = (err: unknown): boolean =>
  (err as ErrorLike | null | undefined)?.response?.status === 404;
